import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import {
  Box,
  Grid2,
  Stack,
  Typography,
  useTheme,
  Zoom,
  Tooltip,
} from "@mui/material";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { InvestigationItemContainer } from "../../components/InvestigationItemContainer";
import { InvestigationButton } from "../../components/InvestigationButton";
import { InvestigationBlockInfo } from "../../components/InvestigationBlockInfo";
import { DrawerContainer } from "../../components/DrawerContainer";
import { returnIsValid, useConstants } from "../../Main/useConstants";
import { validateForm } from "../AddInvolved";
import { SectionedSelect } from "../../../../../MainComponents/Select/SectionedSelect";
import Icon from "../../../../../Icon/Icon";
import _ from "lodash";
import { useReport } from "../../../../../../context/report/ReportContext";

// Vista de los involucrados en un DRAWER
export const InvolvedInfo = ({
  open,
  onClose,
  selectedInvolved,
  setSelectedInvolved,
  handleSaveInvolveds,
  involvedsInInvestigation,
  isLoading,
  justView,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // obtenemos reportState desde el contexto (reemplaza jaguarState)
  const { state: reportState } = useReport();
  const authState = useSelector((state) => state.auth);

  const {
    enums,
    selects: { typeUserLabels },
    involveds: {
      colaboratorOpts,
      statusOpts,
      involvedTypeOpts,
      involvedsKindInfo,
    },
  } = useConstants();

  const [isDeleting, setIsDeleting] = useState(false);

  // Construimos indicatedOpts leyendo desde reportState (mismo comportamiento que antes pero sin redux)
  const indicatedOpts = useMemo(() => {
    const {
      COMPLAINANT,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_VICTIM,
      INVOLVEDS_WITNESS,
      SYSTEM,
      ME,
    } = enums.typeUser;
    const usersWanted = [
      ME,
      COMPLAINANT,
      INVOLVEDS_VICTIM,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_WITNESS,
      SYSTEM,
    ];

    const newIndicatedOpts = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newIndicatedOpts.push({
          key: value,
          options: [{
            value: authState.email,
            label: authState.username || authState.email || t("type_users.ME"),
          }],
        });
        return;
      }
      if (value === COMPLAINANT) {
        newIndicatedOpts.push({
          key: value,
          options: [
            {
              value: COMPLAINANT,
              label: t("involved.reporter"),
            },
          ],
        });
        return;
      }

      // ruta definida en enums.typeUserRoutes (ej: currentReport.investigation.involvedsOffenderInfo)
      const path = _.get(enums, ["typeUserRoutes", value]);
      const node = _.get(reportState, path);
      // Intentamos obtener .formatted (si existe) o fallback a raw o a array vacío
      const fromArray =
        (node &&
          node.formatted &&
          Array.isArray(node.formatted) &&
          node.formatted) ||
        (node && node.raw && Array.isArray(node.raw) && node.raw) ||
        (Array.isArray(node) && node) ||
        [];

      newIndicatedOpts.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    return newIndicatedOpts;
  }, [reportState, enums, typeUserLabels, t, authState]);

  const handleUpdateInvolved = async (newInvolved, oldInvolved) => {
    const newKind = newInvolved.kind;
    const { index: newIndex = null, ...cleanedNewInvolved } = newInvolved;
    const { kind: oldKind, index = null } = oldInvolved;

    const newInvolveds = _.cloneDeep(involvedsInInvestigation) ?? {};
    const ensureBucket = (kind) => {
      if (!Array.isArray(newInvolveds[kind])) newInvolveds[kind] = [];
      return newInvolveds[kind];
    };
    if (oldInvolved.added) { // Para los involucrados que vienen del reporte
      const involvedKind = ensureBucket(oldInvolved.rType);
      const modifiedIndex = involvedKind.findIndex(item=>item.rName===oldInvolved.rName && item.rType===oldInvolved.rType && item.rOccupation===oldInvolved.rOccupation);
      if (modifiedIndex >= 0) {
        involvedKind[modifiedIndex] = newInvolved;
      } else {
        involvedKind.push(newInvolved);
      }
    } else {
      // Para los involucrados que vienen de investigacion
      if (newKind !== oldKind) {
        const oldBucket = ensureBucket(oldKind);
        const newBucket = ensureBucket(newKind);
        if (index !== null && index >= 0 && index < oldBucket.length) {
          oldBucket.splice(index, 1);
        }
        newBucket.push(cleanedNewInvolved);
      } else {
        const involvedKind = ensureBucket(newKind);
        if (index !== null && index >= 0) {
          involvedKind[index] = cleanedNewInvolved;
        } else {
          involvedKind.push(cleanedNewInvolved);
        }
      }
    }
    try {
      await handleSaveInvolveds(newInvolveds);
      setSelectedInvolved(newInvolved);
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveInvolved = async (object) => {
    const { index, kind } = object;
    const newInvolveds = _.cloneDeep(involvedsInInvestigation) ?? {};
    if (Array.isArray(newInvolveds[kind]) && index !== null && index >= 0 && index < newInvolveds[kind].length) {
      newInvolveds[kind].splice(index, 1);
    }
    try {
      await handleSaveInvolveds(newInvolveds);
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = () => {
    handleRemoveInvolved(selectedInvolved);
    setIsDeleting(false);
  };

  const handleClose = () => {
    setSelectedInvolved(null);
    onClose();
  };

  return (
    <DrawerContainer
      open={open}
      onClose={handleClose}
      borderColor={involvedsKindInfo?.[selectedInvolved?.kind]?.color}
    >
      <Box padding="10px 20px 20px 20px">
        <Typography
          fontSize={28}
          fontWeight={600}
          color={involvedsKindInfo?.[selectedInvolved?.kind]?.color}
          marginBottom={2}
        >
          {involvedsKindInfo?.[selectedInvolved?.kind]?.title}
        </Typography>
        <Stack spacing={2}>
          <Data
            selectedInvolved={selectedInvolved}
            handleUpdateInvolved={handleUpdateInvolved}
            involvedsKindInfo={involvedsKindInfo}
            involvedTypeOpts={involvedTypeOpts}
            indicatedOpts={indicatedOpts}
            colaboratorOpts={colaboratorOpts}
            statusOpts={statusOpts}
            isDeleting={isDeleting}
            setIsDeleting={setIsDeleting}
            isLoading={isLoading}
            open={open}
            justView={justView}
          />
          {isDeleting && (
            <Stack
              border={`2px solid ${theme.palette.pink.main}`}
              borderRadius="10px"
              sx={{ padding: "10px 20px 20px 20px" }}
              spacing={1.2}
            >
              <Typography fontSize={28} color="pink" fontWeight={700}>
                {t("involved.important")}
              </Typography>
              <Typography fontSize={16} color="oxfordBlue">
                {t("involved.delete_warning")}
              </Typography>
              <Typography fontSize={16} color="primary" fontWeight={600}>
                {t("involved.confirm_proceed")}
              </Typography>
              <InvestigationButton
                label={t("involved.cancel")}
                variant="main"
                fullWidth
                outlined
                onClick={() => setIsDeleting(false)}
              />
              <InvestigationButton
                label={t("involved.confirm")}
                variant="main"
                fullWidth
                onClick={handleDelete}
              />
            </Stack>
          )}
        </Stack>
      </Box>
    </DrawerContainer>
  );
};

// Componente para editar y ver la informacion del involucrado
const Data = ({
  selectedInvolved,
  handleUpdateInvolved,
  involvedsKindInfo,
  involvedTypeOpts,
  indicatedOpts,
  colaboratorOpts,
  statusOpts,
  isDeleting,
  setIsDeleting,
  isLoading,
  open,
  justView,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const { enums, functions, involveds: { genderOpts, reinsidenceOpts } } = useConstants();

  const [kind, setKind] = useState("");
  const [name, setName] = useState("");
  const [collaborator, setCollaborator] = useState("");
  const [additional, setAdditional] = useState("");
  const [area, setArea] = useState("");
  const [relationship, setRelationship] = useState("");
  const [indicated, setIndicated] = useState(null);
  const [status, setStatus] = useState("");
  const [details, setDetails] = useState("");
  const [gender, setGender] = useState("");
  const [reinsidence, setReinsidence] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  useEffect(() => {
    if (!open) {
      handleClose();
    }
  }, [open]);

  useEffect(() => {
    if (selectedInvolved) {
      setKind(selectedInvolved.kind ?? "");
      setName(selectedInvolved.name ?? "");
      setCollaborator(selectedInvolved.collaborator ?? "");
      setAdditional(selectedInvolved.additional ?? "");
      setArea(selectedInvolved.area ?? "");
      setRelationship(selectedInvolved.relationship ?? "");
      setIndicated(selectedInvolved.indicated ?? null);
      setStatus(
        selectedInvolved.confirm_text ??
          (selectedInvolved.confirm ? "confirmed" : "pending")
      );
      setDetails(selectedInvolved.details ?? "");
      setGender(selectedInvolved.gender ?? "");
      setReinsidence(selectedInvolved.reinsidence ?? "");
    }
    setErrors({});
    setTriedToSave(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, selectedInvolved]);

  // Validate every change
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm({ kind, name, indicated, status });
      setErrors(errors);
    }
  }, [kind, name, indicated, status, triedToSave]);

  const handleSave = async () => {
    const isInternal = collaborator === "internal";
    const [isValid, errors] = validateForm({ kind, name, indicated, status });
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("missing_required_inputs"),
      });
      return;
    }
    await handleUpdateInvolved(
      {
        ...selectedInvolved,
        kind,
        rol: kind,
        name,
        collaborator,
        additional: isInternal ? additional : "", // # empleado
        area: isInternal ? area : "", // area o departamento
        relationship: isInternal ? relationship : "", // puesto
        indicated,
        gender,
        reinsidence,
        validated: false,
        confirm: status === "confirmed" ? true : false,
        confirm_text: status,
        details,
      },
      selectedInvolved
    );
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setKind("");
    setName("");
    setCollaborator("");
    setAdditional("");
    setArea("");
    setRelationship("");
    setIndicated(null);
    setStatus("");
    setDetails("");
    setGender("");
    setReinsidence("");
  };

  const handleClose = () => {
    handleCancel();
    setErrors({});
    setTriedToSave(false);
  };

  return (
    <InvestigationItemContainer
      color={involvedsKindInfo?.[selectedInvolved?.kind]?.color}
      sx={{ padding: "10px 20px 20px 20px" }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        gap={1}
      >
        <Typography
          fontSize={16}
          fontWeight={600}
          color={involvedsKindInfo?.[selectedInvolved?.kind]?.color}
        >
          {selectedInvolved?.name}
        </Typography>
        <Tooltip
          title={
            selectedInvolved?.confirm_text === "confirmed"
              ? t("involved.confirmed")
              : selectedInvolved?.confirm_text === "cant_confirm"
              ? t("involved.not_confirmed")
              : selectedInvolved?.confirm_text === "out"
              ? t("involved.not_participated")
              : selectedInvolved?.confirm_text === true
              ? t("involved.confirmed")
              : t("involved.pending_confirmation")
          }
          placement="bottom"
          arrow
          TransitionComponent={Zoom}
          TransitionProps={{ timeout: 150 }}
        >
          <Box ml={0.5}>
            <Icon
              name={
                selectedInvolved?.confirm_text === "confirmed"
                  ? "bold_tick_circle"
                  : selectedInvolved?.confirm_text === "cant_confirm"
                  ? "bold_danger"
                  : selectedInvolved?.confirm_text === "out"
                  ? "bold_close_circle"
                  : selectedInvolved?.confirm_text === true
                  ? "bold_tick_circle"
                  : "bold_info_circle"
              }
              color={
                selectedInvolved?.confirm_text === "confirmed"
                  ? theme.palette.green.main
                  : selectedInvolved?.confirm_text === "cant_confirm"
                  ? theme.palette.orange.main
                  : selectedInvolved?.confirm_text === "out"
                  ? theme.palette.red.main
                  : selectedInvolved?.confirm_text === true
                  ? theme.palette.green.main
                  : theme.palette.yellow.main
              }
              size={16}
            />
          </Box>
        </Tooltip>
      </Box>

      <hr
        style={{
          color: theme.palette.secondary.main,
          height: "1px",
          border: "none",
        }}
      />

      {!isEditing && (
        <Stack spacing={1.5}>
          {selectedInvolved?.collaborator && (
            <InvestigationBlockInfo
              label={t("involved.collaborator_type")}
              text={
                colaboratorOpts.find(
                  (item) => item.value === selectedInvolved?.collaborator
                )?.label ?? ""
              }
            />
          )}
          {selectedInvolved?.collaborator === "internal" && (
            <>
              {selectedInvolved?.additional && (
                <InvestigationBlockInfo
                  label={t("involved.employee_number")}
                  text={selectedInvolved?.additional ?? ""}
                />
              )}
              {selectedInvolved?.area && (
                <InvestigationBlockInfo
                  label={t("involved.area")}
                  text={selectedInvolved?.area ?? ""}
                />
              )}
              {selectedInvolved?.relationship && (
                <InvestigationBlockInfo
                  label={t("involved.position")}
                  text={selectedInvolved?.relationship ?? ""}
                />
              )}
            </>
          )}
          <InvestigationBlockInfo
            label={t("involved.indicated_by")}
            text={
              selectedInvolved?.indicated?.from === selectedInvolved?.added ? 
              functions.getUserLabel({ from: enums.typeUser.COMPLAINANT }) :
              functions.getUserLabel(selectedInvolved?.indicated) ?? t("involved.other")
            }
          />
          {selectedInvolved?.gender && (
            <InvestigationBlockInfo
              label={t("involved.gender")}
              text={
                genderOpts.find(
                  (item) => item.value === selectedInvolved?.gender
                )?.label ?? ""
              }
            />
          )}
          {selectedInvolved?.reinsidence && (
            <InvestigationBlockInfo
              label={t("involved.reinsidence")}
              text={
                reinsidenceOpts.find(
                  (item) => item.value === selectedInvolved?.reinsidence
                )?.label ?? ""
              }
            />
          )}
          {selectedInvolved?.details && (
            <InvestigationBlockInfo
              label={t("involved.details")}
              text={selectedInvolved?.details ?? ""}
            />
          )}
          {!justView && (
            <Box display="flex" justifyContent="flex-end">
              {!selectedInvolved?.added && (
                <InvestigationButton
                  icon="trash_can"
                  sx={{ marginRight: 1 }}
                  iconColor={
                    isDeleting
                      ? theme.palette.blueGray200.main
                      : theme.palette.red.main
                  }
                  onClick={() => setIsDeleting(true)}
                  disabled={isDeleting}
                  loading={isLoading}
                  size="thin"
                />
              )}
              <InvestigationButton
                label={t("involved.edit")}
                icon="outline_edit"
                iconColor={
                  isDeleting ? theme.palette.blueGray200.main : undefined
                }
                onClick={() => setIsEditing(true)}
                disabled={isDeleting}
                loading={isLoading}
                size="thin"
              />
            </Box>
          )}
        </Stack>
      )}

      {isEditing && (
        <Stack spacing={1.8}>
          <EGBasicSelect
            label={t("involved.type")}
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={involvedTypeOpts}
            isValid={returnIsValid(errors.kind, triedToSave)}
            required={true}
            showSuccessValidation={triedToSave}
            showHelpText={false}
          />
          <EGInput
            label={t("involved.full_name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            isValid={returnIsValid(errors.name, triedToSave)}
            required={true}
            showSuccessValidation={triedToSave}
            showHelpText={false}
          />
          <EGBasicSelect
            label={t("involved.collaborator_type")}
            value={collaborator}
            onChange={(e) => setCollaborator(e.target.value)}
            options={colaboratorOpts}
          />

          {collaborator === "internal" && (
            <>
              <Grid2 size={{ xs: 12, lg: 6 }}>
                <EGInput
                  label={t("involved.employee_number")}
                  value={additional}
                  onChange={(e) => setAdditional(e.target.value)}
                  type="text"
                />
              </Grid2>

              <Grid2 size={{ xs: 12, lg: 6 }}>
                <EGInput
                  label={t("involved.area")}
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, lg: 6 }}>
                <EGInput
                  label={t("involved.position")}
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                />
              </Grid2>
            </>
          )}

          <SectionedSelect
            label={t("involved.indicated_by")}
            value={indicated?.id ?? ""}
            onChange={({ value, section, label }) =>
              setIndicated({ id: value, from: section, label })
            }
            sections={indicatedOpts}
            required
            isValid={returnIsValid(errors?.indicated, triedToSave)}
            fallbackLabel={functions.getUserLabel(indicated)}
            showSuccessValidation={true}
            showHelpText={false}
          />

          <EGBasicSelect
            label={t("involved.gender")}
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            options={genderOpts}
          />

          <EGBasicSelect
            label={t("involved.reinsidence")}
            value={reinsidence}
            onChange={(e) => setReinsidence(e.target.value)}
            options={reinsidenceOpts}
          />

          <EGBasicSelect
            label={t("involved.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={statusOpts}
            isValid={returnIsValid(errors.status, triedToSave)}
            required={true}
            showSuccessValidation={triedToSave}
            showHelpText={false}
          />

          <EGInput
            label={t("involved.details")}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            type="textarea"
            multiline
            rows={5}
          />

          <Box display="flex" flexDirection="column">
            <InvestigationButton
              label={t("involved.cancel")}
              icon="close_icon"
              size="medium"
              fullWidth
              onClick={handleCancel}
              sx={{ marginBottom: 1 }}
            />
            <InvestigationButton
              label={t("involved.save")}
              variant="success"
              size="medium"
              icon="mark_check_bold"
              fullWidth
              onClick={handleSave}
            />
          </Box>
        </Stack>
      )}
    </InvestigationItemContainer>
  );
};
